import { useEffect, useState } from "react";
import { buildRegexI } from "../../text-highlighter/TextHighlighter";

type FilterValueType = string | string[] | number;
type AssociativeArrayType = Record<string, FilterValueType>;

const moduleState = {
  filters: {} as Record<string, AssociativeArrayType>,
  filterPatterns: {} as Record<string, Record<string, RegExp>>,
};

type useFiltersType = {
  pageName: string;
};

const useFilters = ({ pageName }: useFiltersType) => {
  const [data, setData] = useState<any>();
  const [filteredData, setFilteredData] = useState<any>();

  if (!moduleState.filters[pageName]) {
    moduleState.filters[pageName] = {};
  }

  if (!moduleState.filterPatterns[pageName]) {
    moduleState.filterPatterns[pageName] = {};
  }

  useEffect(() => {
    if (data && data.length) {
      doFilterData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const getFilterValue = (filterName: string) => {
    return moduleState.filters[pageName][filterName];
  };

  const setFilterValue = (
    filterName: string,
    filterValue: string | string[],
    ignoreChars?: string,
    exactMatch?: boolean
  ) => {
    const oldFilterValue = moduleState.filters[pageName][filterName];
    const newFilterValue = filterValue;

    if (oldFilterValue === newFilterValue) {
      return;
    }
    moduleState.filters[pageName][filterName] = newFilterValue;

    if (Array.isArray(newFilterValue)) {
      //LFC: We don't use /i for case-insensitive because we want to allow case-sensitive search
      // for SELECT options, to prevent "Assigned" matching rows with status "Unassigned"
      // SELECT options are the only cases that pass an array to support multiple fitlers
      if (exactMatch) {
        const filterPattern = new RegExp(
          "^" + (newFilterValue.join("|") || [".*"]) + "$",
          ""
        );
        moduleState.filterPatterns[pageName][filterName] = filterPattern;
      } else {
        const filterPattern = new RegExp(newFilterValue.join("|") || ".*", "");
        moduleState.filterPatterns[pageName][filterName] = filterPattern;
      }
    } else {
      const filterPattern = buildRegexI(
        newFilterValue,
        ignoreChars,
        exactMatch
      );
      moduleState.filterPatterns[pageName][filterName] = filterPattern;
    }
    doFilterData();
  };

  const doFilterData = () => {
    const filteredData = data?.filter((dataRow: any) => {
      for (const filterName in moduleState.filterPatterns[pageName]) {
        if (moduleState.filters[pageName][filterName]) {
          const cellValue = filterName
            .split(".")
            .reduce((acc, part) => acc && acc[part], dataRow);
          if (
            !moduleState.filterPatterns[pageName][filterName].test(cellValue)
          ) {
            return false;
          }
        }
      }
      return true;
    });
    setFilteredData(filteredData);
  };

  return {
    setFilterValue,
    getFilterValue,
    setData,
    data,
    filteredData: filteredData ?? data,
  };
};

export default useFilters;
